import posthog from "posthog-js";

import type { MouseEvent } from "react";

import type { SetupFilter } from "@/lib/setup-checklist-sections";

import type { SetupItemDetail } from "@/types/setup-checklist";

/** Where a link to the full-page guide was clicked; `other` is the menu, Back, a typed URL or a reload. */
export type SetupGuideOpenSource =
  | "settings_home"
  | "footer_card"
  | "onboarding_panel"
  | "sidebar"
  | "other";

/** Which of the two views an item row was used in. */
export type SetupSurface = "page" | "sidebar";

export type SetupItemAction =
  | "expand"
  | "open"
  | "ask_decoda"
  | "docs"
  | "skip"
  | "complete"
  | "unskip"
  | "uncomplete";

// A note older than this belongs to a click that never reached the page.
const SOURCE_NOTE_TTL_MS = 10_000;
let sourceNote: { source: SetupGuideOpenSource; at: number } | null = null;

/** Called by a link to the page, so the page's open event can say where it came from. */
export function noteSetupGuideOpenSource(
  event: MouseEvent,
  source: SetupGuideOpenSource,
) {
  // A new tab loads without this module's state, so it would record `other` anyway.
  if (event.metaKey || event.ctrlKey) return;
  // Already on the page, the link doesn't remount it, so the note would sit unread.
  if (window.location.pathname.endsWith("/settings/setup-guide")) return;
  sourceNote = { source, at: Date.now() };
}

/** Called once when the full page mounts, however the user got there. */
export function captureSetupGuideOpened(filter: SetupFilter | null) {
  const source =
    sourceNote && Date.now() - sourceNote.at < SOURCE_NOTE_TTL_MS
      ? sourceNote.source
      : "other";
  sourceNote = null;
  posthog.capture("setup_guide_opened", { source, filter: filter ?? "all" });
}

/** One event for every way a setup row gets used, for the `setup_item_actions` query. */
export function captureSetupItemAction(
  item: SetupItemDetail,
  action: SetupItemAction,
  surface: SetupSurface,
) {
  posthog.capture("setup_item_action", {
    action,
    surface,
    item_key: item.itemKey,
    group: item.definition.group,
    importance: item.definition.importance,
    critical: item.definition.critical,
    status: item.status,
  });
}
